import { useEffect, useRef, useState } from 'react'
import './LoadingScreenNew.css'

// Max wait before auto-skipping (ms)
const MAX_WAIT_MS = 8000

function LoadingScreenNew({ onFinished }) {
  const [fade, setFade] = useState(false)
  const [videoVisible, setVideoVisible] = useState(false)
  const videoRef = useRef(null)
  const doneRef = useRef(false)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    // React JSX `muted` prop only sets the HTML attribute, not the DOM property.
    // Mobile autoplay policy checks the DOM property — set it explicitly to ensure
    // muted autoplay is allowed on Android/iOS.
    video.muted = true

    if (typeof video.disablePictureInPicture !== 'undefined') {
      video.disablePictureInPicture = true
    }

    const finish = () => {
      if (doneRef.current) return
      doneRef.current = true
      setFade(true)
      setTimeout(() => { if (onFinished) onFinished() }, 900)
    }

    const maxTimer = setTimeout(finish, MAX_WAIT_MS)

    const handleEnded = () => {
      clearTimeout(maxTimer)
      finish()
    }

    const handleError = () => {
      // networkState 3 = NETWORK_NO_SOURCE: all <source> elements exhausted.
      // On some Android Chrome versions, `error` fires on <video> during the
      // WebM→MP4 source fallback transition — do NOT finish in that case.
      if (video.networkState === 3) {
        clearTimeout(maxTimer)
        finish()
      }
    }

    const showVideo = () => setVideoVisible(true)

    video.addEventListener('ended', handleEnded)
    video.addEventListener('error', handleError)
    // `playing` is more reliable than `canplay` on mobile — fires when playback actually starts
    video.addEventListener('canplay', showVideo)
    video.addEventListener('playing', showVideo)

    // Race condition: video already ready from cache before listener attached
    if (video.readyState >= 3) setVideoVisible(true)

    // Interaction-based fallback when autoplay is blocked by the browser
    let interactionListenersActive = false
    const cleanupInteraction = () => {
      if (!interactionListenersActive) return
      document.removeEventListener('touchstart', tryOnInteraction)
      document.removeEventListener('click', tryOnInteraction)
      interactionListenersActive = false
    }

    const tryOnInteraction = () => {
      cleanupInteraction()
      video.play().catch(() => {
        clearTimeout(maxTimer)
        finish()
      })
    }

    const playPromise = video.play()
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // Don't finish immediately — wait for the user's first touch/click
        // and retry. maxTimer still handles the hard timeout.
        interactionListenersActive = true
        document.addEventListener('touchstart', tryOnInteraction, { once: true, passive: true })
        document.addEventListener('click', tryOnInteraction, { once: true })
      })
    }

    return () => {
      clearTimeout(maxTimer)
      cleanupInteraction()
      video.removeEventListener('ended', handleEnded)
      video.removeEventListener('error', handleError)
      video.removeEventListener('canplay', showVideo)
      video.removeEventListener('playing', showVideo)
    }
  }, [onFinished])

  return (
    <div className={`loading-screen-new${fade ? ' loading-screen-new--fade-out' : ''}`}>
      <video
        ref={videoRef}
        className={`loading-screen-new__video${videoVisible ? ' loading-screen-new__video--visible' : ''}`}
        preload="auto"
        autoPlay
        muted
        playsInline
        disablePictureInPicture
        controlsList="nodownload nofullscreen noplaybackrate"
      >
        <source src="/video/loading.mp4" type="video/mp4" />
      </video>
    </div>
  )
}

export default LoadingScreenNew
